import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { tienePermiso } from '../utils/navPermisos';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Building, Mail, Palette, Save, Loader2, 
  AlertCircle, CheckCircle2, Image, Upload, Sparkles, Moon, Briefcase, Settings,
  Wand2, FileText, Trash2, RefreshCw, X, Globe
} from 'lucide-react';
import CentroAyudaConfigCards from '../modules/centroAyuda/CentroAyudaConfigCards';
import { DEFAULT_AVISO_PRIVACIDAD } from '../modules/centroAyuda/avisoPrivacidad';
import { ShieldCheck } from 'lucide-react';

// Presets de colores
const COLOR_PRESETS = {
  imagina: {
    nombre: 'Imagina',
    descripcion: 'Paleta corporativa Imagina',
    icon: Sparkles,
    colores: {
      primario: '#C9A85C',
      secundario: '#C5B8A2',
      terciario: '#5A7A7A',
      cuaternario: '#5A7A7A',
      fondo: '#FFFFFF',
      superficie: '#F5F5F5',
      fondo_cabecera: '#333333',
      texto: '#333333',
      texto_secundario: '#666666',
      error: '#DC3545'
    }
  },
  oscuro: {
    nombre: 'Modo Oscuro',
    descripcion: 'Tema oscuro elegante',
    icon: Moon,
    colores: {
      primario: '#D4B86A',
      secundario: '#A89878',
      terciario: '#6B9A9A',
      cuaternario: '#6B9A9A',
      fondo: '#1A1A1A',
      superficie: '#2D2D2D',
      fondo_cabecera: '#0D0D0D',
      texto: '#FFFFFF',
      texto_secundario: '#A0A0A0',
      error: '#FF5252'
    }
  },
  clasico: {
    nombre: 'Clásico',
    descripcion: 'Azul corporativo tradicional',
    icon: Briefcase,
    colores: {
      primario: '#1a1a2e',
      secundario: '#16213e',
      terciario: '#e94560',
      cuaternario: '#16213e',
      fondo: '#FFFFFF',
      superficie: '#F8FAFC',
      fondo_cabecera: '#1a1a2e',
      texto: '#1E293B',
      texto_secundario: '#64748B',
      error: '#EF4444'
    }
  }
};

// Colores por defecto
const DEFAULT_COLORS = COLOR_PRESETS.imagina.colores;

const EmpresaPage = () => {
  const { api, empresa: currentEmpresa, user, updateEmpresa } = useAuth();
  // Página "Empresa" gobernada por la matriz de roles: editar => puede modificar.
  const isAdmin = () => tienePermiso(user, 'empresa', 'editar');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);
  
  const [empresa, setEmpresa] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    correo_principal: '',
    correos_copia: '',
    colores_tema: { ...DEFAULT_COLORS },
    envio_automatico_resolucion: false,
    plazo_atencion_dias: 15,
    notif_asignacion_responsable: true,
    notif_nuevo_reclamo: true,
    notif_dias_sin_gestion: 3,
    notif_dias_sin_gestion_activo: true,
    notif_dias_antes_vencer: 3,
    notif_dias_antes_vencer_activo: true,
    notif_reclamo_vencido: true,
    notif_reclamo_terminado: true,
    ia_resoluciones_habilitado: false,
    ia_resoluciones_estructura: '',
    ia_resoluciones_archivos: [],
    ia_resoluciones_sincronizado_at: null,
    dominio_personalizado: '',
    modulo_tickets_activo: false,
    aviso_privacidad: '',
  });
  
  const [iaArchivosLoading, setIaArchivosLoading] = useState(false);
  const [iaSincronizando, setIaSincronizando] = useState(false);
  const iaFileInputRef = React.useRef(null);

  const fetchEmpresa = useCallback(async (signal) => {
    setLoading(true);
    try {
      const response = await api.get('/mi-empresa', { signal });
      setEmpresa(response.data);
      
      // Mergear colores existentes con defaults para compatibilidad
      const existingColors = response.data.colores_tema || {};
      const mergedColors = { ...DEFAULT_COLORS };
      
      // Mapear colores antiguos si existen
      if (existingColors.primario) mergedColors.primario = existingColors.primario;
      if (existingColors.secundario) mergedColors.secundario = existingColors.secundario;
      if (existingColors.acento) mergedColors.terciario = existingColors.acento;
      if (existingColors.terciario) mergedColors.terciario = existingColors.terciario;
      if (existingColors.cuaternario) mergedColors.cuaternario = existingColors.cuaternario;
      if (existingColors.fondo) mergedColors.fondo = existingColors.fondo;
      if (existingColors.superficie) mergedColors.superficie = existingColors.superficie;
      if (existingColors.fondo_cabecera) mergedColors.fondo_cabecera = existingColors.fondo_cabecera;
      if (existingColors.texto) mergedColors.texto = existingColors.texto;
      if (existingColors.texto_secundario) mergedColors.texto_secundario = existingColors.texto_secundario;
      if (existingColors.error) mergedColors.error = existingColors.error;
      
      setFormData({
        nombre: response.data.nombre || '',
        direccion: response.data.direccion || '',
        correo_principal: response.data.correo_principal || '',
        correos_copia: response.data.correos_copia || '',
        colores_tema: mergedColors,
        envio_automatico_resolucion: response.data.envio_automatico_resolucion || false,
        plazo_atencion_dias: response.data.plazo_atencion_dias ?? 15,
        notif_asignacion_responsable: response.data.notif_asignacion_responsable ?? true,
        notif_nuevo_reclamo: response.data.notif_nuevo_reclamo ?? true,
        notif_dias_sin_gestion: response.data.notif_dias_sin_gestion ?? 3,
        notif_dias_sin_gestion_activo: response.data.notif_dias_sin_gestion_activo ?? true,
        notif_dias_antes_vencer: response.data.notif_dias_antes_vencer ?? 3,
        notif_dias_antes_vencer_activo: response.data.notif_dias_antes_vencer_activo ?? true,
        notif_reclamo_vencido: response.data.notif_reclamo_vencido ?? true,
        notif_reclamo_terminado: response.data.notif_reclamo_terminado ?? true,
        dominio_personalizado: response.data.dominio_personalizado || '',
        modulo_tickets_activo: !!response.data.modulo_tickets_activo,
        aviso_privacidad: (response.data.aviso_privacidad ?? '') || DEFAULT_AVISO_PRIVACIDAD,
      });
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error fetching empresa:', err);
      setError('No se pudo cargar la información de la empresa');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    const controller = new AbortController();
    fetchEmpresa(controller.signal);
    return () => controller.abort();
  }, [fetchEmpresa]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const dataToSave = {
        ...formData,
        ia_resoluciones_habilitado: empresa?.ia_resoluciones_habilitado || false,
        ia_resoluciones_estructura: empresa?.ia_resoluciones_estructura || '',
      };
      const response = await api.put('/mi-empresa', dataToSave);
      // Actualizar el contexto global para que los colores se apliquen inmediatamente
      updateEmpresa(response.data);
      setEmpresa(response.data);
      setSuccess('Configuración guardada exitosamente. Los colores se aplicaron en toda la plataforma.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de archivo no permitido. Use JPG, PNG, GIF o WEBP');
      return;
    }

    // Validar tamaño (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('El archivo excede el tamaño máximo de 2MB');
      return;
    }

    setUploadingLogo(true);
    setError('');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/mi-empresa/logo', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Actualizar empresa con el nuevo logo
      setEmpresa({ ...empresa, logo_url: response.data.logo_url });
      setSuccess('Logo actualizado exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al subir el logo');
    } finally {
      setUploadingLogo(false);
      // Limpiar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const updateColor = (key, value) => {
    setFormData({
      ...formData,
      colores_tema: {
        ...formData.colores_tema,
        [key]: value,
      },
    });
  };

  const handleToggleModuloTickets = async (nuevoValor) => {
    try {
      const response = await api.put('/mi-empresa', { modulo_tickets_activo: nuevoValor });
      updateEmpresa(response.data);
      setEmpresa(response.data);
      setFormData((prev) => ({ ...prev, modulo_tickets_activo: nuevoValor }));
      toast.success(`Módulo Centro de Ayuda ${nuevoValor ? 'activado' : 'desactivado'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar el estado del módulo');
    }
  };

  const applyPreset = (presetKey) => {
    const preset = COLOR_PRESETS[presetKey];
    if (preset) {
      setFormData({
        ...formData,
        colores_tema: { ...preset.colores },
      });
      setSuccess(`Preset "${preset.nombre}" aplicado. Guarda los cambios para confirmar.`);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  // Componente para input de color
  const ColorInput = ({ colorKey, label, description }) => (
    <div className="space-y-2">
      <Label htmlFor={`color_${colorKey}`} className="text-sm font-medium">
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          id={`color_${colorKey}`}
          data-testid={`color-${colorKey}-input`}
          value={formData.colores_tema[colorKey] || DEFAULT_COLORS[colorKey]}
          onChange={(e) => updateColor(colorKey, e.target.value)}
          disabled={!isAdmin()}
          className="w-12 h-12 rounded-lg cursor-pointer border-2 border-slate-200 hover:border-slate-300 transition-colors"
          style={{ padding: '2px' }}
        />
        <div className="flex-1">
          <Input
            value={formData.colores_tema[colorKey] || DEFAULT_COLORS[colorKey]}
            onChange={(e) => updateColor(colorKey, e.target.value)}
            disabled={!isAdmin()}
            className="font-mono text-xs uppercase"
            maxLength={7}
          />
          {description && (
            <p className="text-xs text-slate-400 mt-1">{description}</p>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Error de carga (p. ej. backend no disponible): mostrar tarjeta amable con reintento
  if (error && !empresa) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="empresa-load-error">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">No se pudo cargar la información</h2>
          <p className="text-slate-500 text-sm mb-5">
            No pudimos obtener los datos de la empresa. Verifica tu conexión e inténtalo de nuevo.
          </p>
          <Button onClick={() => fetchEmpresa()} data-testid="empresa-retry-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="empresa-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Configuración de Empresa</h1>
        <p className="text-slate-500 text-sm mt-1">
          {isAdmin() ? 'Administre la configuración y personalización visual de su empresa' : 'Información de la empresa'}
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="animate-fadeIn">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-emerald-200 bg-emerald-50 text-emerald-800 animate-fadeIn">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Información General */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building className="w-5 h-5" />
                Información General
              </CardTitle>
              <CardDescription>Datos básicos de la empresa</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre de la Empresa</Label>
                <Input
                  id="nombre"
                  data-testid="nombre-input"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  disabled={!isAdmin()}
                  placeholder="Nombre del grupo inmobiliario"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="direccion">Dirección Fiscal</Label>
                <Textarea
                  id="direccion"
                  data-testid="direccion-input"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  disabled={!isAdmin()}
                  placeholder="Av. Ejemplo 123, Distrito, Ciudad"
                  rows={2}
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="codigo" className="flex items-center gap-2">
                  Codigo de Empresa
                  <span className="text-xs text-slate-400">(URL publica)</span>
                </Label>
                <Input
                  id="codigo"
                  value={empresa?.codigo || ''}
                  disabled
                  className="font-mono bg-slate-50"
                />
                <p className="text-xs text-slate-500">
                  URL del formulario: /reclamos/{empresa?.codigo}
                </p>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="dominio_personalizado" className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Dominio Personalizado
                </Label>
                <Input
                  id="dominio_personalizado"
                  data-testid="dominio-personalizado-input"
                  value={formData.dominio_personalizado}
                  onChange={(e) => setFormData({ ...formData, dominio_personalizado: e.target.value })}
                  disabled={!isAdmin()}
                  placeholder="ej: reclamos.miempresa.com"
                  className="font-mono"
                />
                <p className="text-xs text-slate-500">
                  Al configurar un dominio personalizado, los usuarios de esta empresa accederan directamente desde ese dominio sin necesidad de seleccionar la empresa en el login. Las rutas disponibles seran:
                </p>
                {formData.dominio_personalizado && (
                  <div className="text-xs text-slate-600 bg-slate-50 rounded-md p-3 space-y-1 font-mono">
                    <p><span className="text-slate-400">Login:</span> {formData.dominio_personalizado}/</p>
                    <p><span className="text-slate-400">Reclamos:</span> {formData.dominio_personalizado}/reclamos</p>
                    <p><span className="text-slate-400">Consulta:</span> {formData.dominio_personalizado}/consulta</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Configuración de Correos */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Mail className="w-5 h-5" />
                Configuración de Correos
              </CardTitle>
              <CardDescription>Correos para notificaciones de reclamos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="correo_principal">Correo Principal (Remitente)</Label>
                <Input
                  id="correo_principal"
                  type="email"
                  data-testid="correo-principal-input"
                  value={formData.correo_principal}
                  onChange={(e) => setFormData({ ...formData, correo_principal: e.target.value })}
                  disabled={!isAdmin()}
                  placeholder="notificaciones@empresa.com"
                />
                <p className="text-xs text-slate-500">
                  Este correo aparecerá como remitente en las notificaciones
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="correos_copia">Correos en Copia (CCO)</Label>
                <Textarea
                  id="correos_copia"
                  data-testid="correos-copia-input"
                  value={formData.correos_copia}
                  onChange={(e) => setFormData({ ...formData, correos_copia: e.target.value })}
                  disabled={!isAdmin()}
                  placeholder="reclamos@empresa.com;legal@empresa.com"
                  rows={3}
                />
                <p className="text-xs text-slate-500">
                  Separe múltiples correos con punto y coma (;)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* ============================================================
              CENTRO DE AYUDA — Sistema de Tickets (módulo aislado)
              ============================================================ */}
          <CentroAyudaConfigCards
            api={api}
            isAdmin={isAdmin()}
            moduloActivo={formData.modulo_tickets_activo}
            onToggleModulo={handleToggleModuloTickets}
            themeColor={formData.colores_tema?.primario || '#C9A85C'}
          />

          {/* Aviso de Privacidad del Kiosko Presencial */}
          {formData.modulo_tickets_activo && (
          <Card className="border-slate-200 lg:col-span-2" data-testid="seccion-aviso-privacidad">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ShieldCheck className="w-5 h-5" />
                Aviso de Privacidad del Kiosko
              </CardTitle>
              <CardDescription>
                Texto legal (Ley N° 29733) mostrado al cliente en el Kiosko de Registro Presencial (pasos 1 y 3).
                Puede incluir enlaces con HTML básico, por ejemplo:
                {' '}<code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">&lt;a href="https://..."&gt;texto&lt;/a&gt;</code>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                id="aviso_privacidad"
                data-testid="aviso-privacidad-input"
                value={formData.aviso_privacidad}
                onChange={(e) => setFormData({ ...formData, aviso_privacidad: e.target.value })}
                disabled={!isAdmin()}
                rows={8}
                className="font-mono text-xs leading-relaxed"
                placeholder="Escribe aquí el aviso de privacidad que verá el cliente en el kiosko…"
              />
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-slate-500">
                  Se admite HTML básico (enlaces). El cliente lo verá con letra pequeña dentro de un recuadro.
                </p>
                {isAdmin() && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="aviso-privacidad-restaurar-btn"
                    onClick={() => setFormData({ ...formData, aviso_privacidad: DEFAULT_AVISO_PRIVACIDAD })}
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Restaurar texto por defecto
                  </Button>
                )}
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="aviso-privacidad-preview">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Vista previa</p>
                <div
                  className="text-[11px] leading-relaxed text-slate-600 [&_a]:text-blue-600 [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: formData.aviso_privacidad || '' }}
                />
              </div>
            </CardContent>
          </Card>
          )}

          {/* Resoluciones Inteligentes */}
          <Card className="border-slate-200 lg:col-span-2" data-testid="seccion-resoluciones-ia">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Wand2 className="w-5 h-5 text-violet-600" />
                Resoluciones Inteligentes
              </CardTitle>
              <CardDescription>Generación de resoluciones asistida por inteligencia artificial</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Switch
                  checked={empresa.ia_resoluciones_habilitado}
                  onCheckedChange={(checked) => setEmpresa({ ...empresa, ia_resoluciones_habilitado: checked })}
                  data-testid="switch-ia-habilitado"
                  disabled={!isAdmin()}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label className="text-sm font-medium">Habilitar Resoluciones con Inteligencia Artificial</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Permite generar borradores de resoluciones usando IA</p>
                </div>
              </div>

              {empresa.ia_resoluciones_habilitado && (
                <div className="space-y-6">
                  {/* Estructura de la resolución */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Estructura de la Resolución</Label>
                    <p className="text-xs text-slate-500">Defina las secciones que la IA debe seguir al generar resoluciones</p>
                    <Textarea
                      value={empresa.ia_resoluciones_estructura || ''}
                      onChange={(e) => setEmpresa({ ...empresa, ia_resoluciones_estructura: e.target.value })}
                      placeholder={"PRIMERO: Antecedentes del reclamo\nSEGUNDO: Análisis del caso según normativa vigente\nTERCERO: Decisión y medidas adoptadas\nCUARTO: Plazo de ejecución"}
                      rows={5}
                      className="text-sm"
                      disabled={!isAdmin()}
                      data-testid="textarea-ia-estructura"
                    />
                  </div>

                  {/* Archivos de ejemplo */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Resoluciones de Ejemplo</Label>
                    <p className="text-xs text-slate-500">Suba resoluciones anteriores aprobadas como referencia de estilo (PDF/DOCX, máx. 10MB)</p>
                    
                    {(empresa.ia_resoluciones_archivos || []).length > 0 && (
                      <div className="space-y-1.5">
                        {(empresa.ia_resoluciones_archivos || []).map((archivo) => (
                          <div key={archivo.id} className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2 text-sm border border-slate-200">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                              <span className="truncate">{archivo.nombre}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                              disabled={!isAdmin()}
                              onClick={async () => {
                                try {
                                  const res = await api.delete(`/mi-empresa/ia-resoluciones/archivos/${archivo.id}`);
                                  setEmpresa({ ...empresa, ia_resoluciones_archivos: res.data.archivos });
                                  toast.success('Archivo eliminado');
                                } catch (err) {
                                  toast.error('Error al eliminar archivo');
                                }
                              }}
                              data-testid={`btn-eliminar-ia-archivo-${archivo.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    <input
                      type="file"
                      ref={iaFileInputRef}
                      className="hidden"
                      accept=".pdf,.doc,.docx"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setIaArchivosLoading(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const res = await api.post('/mi-empresa/ia-resoluciones/archivos', formData);
                          setEmpresa({ ...empresa, ia_resoluciones_archivos: res.data.archivos });
                          toast.success('Archivo subido exitosamente');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Error al subir archivo');
                        } finally {
                          setIaArchivosLoading(false);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => iaFileInputRef.current?.click()}
                      disabled={iaArchivosLoading || !isAdmin()}
                      data-testid="btn-subir-ia-archivo"
                    >
                      {iaArchivosLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                      Subir archivo
                    </Button>
                  </div>

                  {/* Botón Sincronizar */}
                  <div className="flex items-center gap-3 p-4 bg-violet-50 rounded-lg border border-violet-200">
                    <Button
                      onClick={async () => {
                        setIaSincronizando(true);
                        try {
                          const res = await api.post('/mi-empresa/ia-resoluciones/sincronizar');
                          toast.success(res.data.message);
                          fetchEmpresa();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Error al sincronizar');
                        } finally {
                          setIaSincronizando(false);
                        }
                      }}
                      disabled={iaSincronizando || !isAdmin() || !(empresa.ia_resoluciones_archivos || []).length}
                      className="bg-violet-600 hover:bg-violet-700"
                      data-testid="btn-sincronizar-ia"
                    >
                      {iaSincronizando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Sincronizar con IA
                    </Button>
                    {empresa.ia_resoluciones_sincronizado_at && (
                      <span className="text-xs text-slate-500" data-testid="ia-ultima-sincronizacion">
                        Última sincronización: {new Date(empresa.ia_resoluciones_sincronizado_at).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Configuraciones Generales - Ubicado antes de Colores */}
          <Card className="border-slate-200 lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="w-5 h-5" />
                Configuraciones Generales
              </CardTitle>
              <CardDescription>Opciones de comportamiento del sistema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Notificaciones de Resoluciones */}
              <div>
                <h4 className="font-medium text-slate-800 mb-4">Notificaciones de Resoluciones</h4>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <Switch
                    id="envio-automatico"
                    checked={formData.envio_automatico_resolucion}
                    onCheckedChange={(checked) => setFormData({ ...formData, envio_automatico_resolucion: checked })}
                    disabled={!isAdmin()}
                    data-testid="envio-automatico-switch"
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <Label htmlFor="envio-automatico" className="font-medium cursor-pointer">
                      Envío automático de resolución al consumidor
                    </Label>
                    <p className="text-sm text-slate-500 mt-1">
                      Cuando una resolución es aprobada por el área legal, se enviará automáticamente un correo al reclamante con el contenido de la resolución.
                    </p>
                    {!formData.envio_automatico_resolucion && (
                      <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Si está desactivado, el envío será manual desde el detalle de la resolución.
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {/* Plazo de Atención de Reclamos */}
              <div>
                <h4 className="font-medium text-slate-800 mb-4">Plazo de Atención</h4>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex-1">
                    <Label htmlFor="plazo-atencion" className="font-medium">
                      Plazo atención reclamos (días hábiles)
                    </Label>
                    <p className="text-sm text-slate-500 mt-1">
                      Plazo legal en días hábiles para gestionar el reclamo y enviar una resolución al reclamante.
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <Input
                        id="plazo-atencion"
                        type="number"
                        min={1}
                        value={formData.plazo_atencion_dias}
                        onChange={(e) => setFormData({ ...formData, plazo_atencion_dias: parseInt(e.target.value) || '' })}
                        disabled={!isAdmin()}
                        className="w-24 bg-white"
                        data-testid="plazo-atencion-input"
                      />
                      <span className="text-sm text-slate-500">días hábiles</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Notificaciones por Correo */}
              <div>
                <h4 className="font-medium text-slate-800 mb-4">Notificaciones por Correo</h4>
                <div className="space-y-3">
                  {/* Alerta 0: Nuevo reclamo */}
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <Switch
                      checked={formData.notif_nuevo_reclamo}
                      onCheckedChange={(checked) => setFormData({ ...formData, notif_nuevo_reclamo: checked })}
                      disabled={!isAdmin()}
                      data-testid="notif-nuevo-reclamo-switch"
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label className="font-medium">Nuevo reclamo</Label>
                      <p className="text-sm text-slate-500 mt-1">Se envía correo a todos los supervisores cada vez que se registra un nuevo reclamo en la plataforma. Se incluye en copia oculta (CCO) a los correos configurados.</p>
                    </div>
                  </div>
                  {/* Alerta 1: Asignación responsable */}
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <Switch
                      checked={formData.notif_asignacion_responsable}
                      onCheckedChange={(checked) => setFormData({ ...formData, notif_asignacion_responsable: checked })}
                      disabled={!isAdmin()}
                      data-testid="notif-asignacion-switch"
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label className="font-medium">Al asignar responsable</Label>
                      <p className="text-sm text-slate-500 mt-1">Se envía correo al responsable asignado con los datos del reclamo y el plazo de atención.</p>
                    </div>
                  </div>
                  {/* Alerta 2: Días sin gestión */}
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <Switch
                      checked={formData.notif_dias_sin_gestion_activo}
                      onCheckedChange={(checked) => setFormData({ ...formData, notif_dias_sin_gestion_activo: checked })}
                      disabled={!isAdmin()}
                      data-testid="notif-sin-gestion-switch"
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label className="font-medium">Reclamo sin gestión</Label>
                      <p className="text-sm text-slate-500 mt-1">Se envía correo a supervisores cuando un reclamo nuevo lleva N días hábiles sin ser atendido. Se incluye en copia oculta (CCO) a los correos configurados.</p>
                      {formData.notif_dias_sin_gestion_activo && (
                        <div className="mt-2 flex items-center gap-2">
                          <Input
                            type="number"
                            min={1}
                            value={formData.notif_dias_sin_gestion}
                            onChange={(e) => setFormData({ ...formData, notif_dias_sin_gestion: parseInt(e.target.value) || '' })}
                            disabled={!isAdmin()}
                            className="w-20 bg-white"
                            data-testid="notif-sin-gestion-dias"
                          />
                          <span className="text-sm text-slate-500">días hábiles</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Alerta 3: Días antes de vencer */}
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <Switch
                      checked={formData.notif_dias_antes_vencer_activo}
                      onCheckedChange={(checked) => setFormData({ ...formData, notif_dias_antes_vencer_activo: checked })}
                      disabled={!isAdmin()}
                      data-testid="notif-antes-vencer-switch"
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label className="font-medium">Próximo a vencer</Label>
                      <p className="text-sm text-slate-500 mt-1">Se envía correo al responsable y supervisores cuando faltan N días hábiles para vencer el plazo.</p>
                      {formData.notif_dias_antes_vencer_activo && (
                        <div className="mt-2 flex items-center gap-2">
                          <Input
                            type="number"
                            min={1}
                            value={formData.notif_dias_antes_vencer}
                            onChange={(e) => setFormData({ ...formData, notif_dias_antes_vencer: parseInt(e.target.value) || '' })}
                            disabled={!isAdmin()}
                            className="w-20 bg-white"
                            data-testid="notif-antes-vencer-dias"
                          />
                          <span className="text-sm text-slate-500">días hábiles antes del vencimiento</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Alerta 4: Reclamo vencido */}
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <Switch
                      checked={formData.notif_reclamo_vencido}
                      onCheckedChange={(checked) => setFormData({ ...formData, notif_reclamo_vencido: checked })}
                      disabled={!isAdmin()}
                      data-testid="notif-vencido-switch"
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label className="font-medium">Reclamo vencido</Label>
                      <p className="text-sm text-slate-500 mt-1">Se envía correo al responsable, supervisores y administradores cuando un reclamo ha excedido el plazo legal. Se incluye en copia oculta (CCO) a los correos configurados.</p>
                    </div>
                  </div>
                  {/* Alerta 5: Reclamo terminado */}
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <Switch
                      checked={formData.notif_reclamo_terminado}
                      onCheckedChange={(checked) => setFormData({ ...formData, notif_reclamo_terminado: checked })}
                      disabled={!isAdmin()}
                      data-testid="notif-terminado-switch"
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label className="font-medium">Reclamo terminado</Label>
                      <p className="text-sm text-slate-500 mt-1">Se envía correo al responsable y supervisores cuando un reclamo se cierra exitosamente.</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personalización de Colores - Expandido */}
          <Card className="border-slate-200 lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Palette className="w-5 h-5" />
                Personalización de Colores
              </CardTitle>
              <CardDescription>
                Personaliza los colores de toda la plataforma. Los cambios se aplican automáticamente al guardar.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Presets */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Presets de Colores</Label>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(COLOR_PRESETS).map(([key, preset]) => {
                    const IconComponent = preset.icon;
                    return (
                      <Button
                        key={key}
                        type="button"
                        variant="outline"
                        onClick={() => applyPreset(key)}
                        disabled={!isAdmin()}
                        className="flex items-center gap-2 hover:border-slate-400"
                        data-testid={`preset-${key}-btn`}
                      >
                        <IconComponent className="w-4 h-4" />
                        {preset.nombre}
                      </Button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Selecciona un preset para aplicar una paleta completa de colores
                </p>
              </div>

              <Separator />

              {/* Grid de colores */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Colores Principales */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-slate-700 border-b pb-2">Colores Principales</h4>
                  <ColorInput 
                    colorKey="primario" 
                    label="Primario" 
                    description="Botones, CTAs"
                  />
                  <ColorInput 
                    colorKey="secundario" 
                    label="Secundario" 
                    description="Hover, bordes"
                  />
                  <ColorInput 
                    colorKey="terciario" 
                    label="Terciario" 
                    description="Sidebar, badges"
                  />
                  <ColorInput 
                    colorKey="cuaternario" 
                    label="Cuaternario" 
                    description="Hover secundario"
                  />
                </div>

                {/* Fondos */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-slate-700 border-b pb-2">Fondos</h4>
                  <ColorInput 
                    colorKey="fondo" 
                    label="Fondo General" 
                    description="Fondo de la app"
                  />
                  <ColorInput 
                    colorKey="superficie" 
                    label="Superficie" 
                    description="Tarjetas, paneles"
                  />
                  <ColorInput 
                    colorKey="fondo_cabecera" 
                    label="Cabecera Form. Público" 
                    description="Header del formulario"
                  />
                </div>

                {/* Textos */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-slate-700 border-b pb-2">Textos</h4>
                  <ColorInput 
                    colorKey="texto" 
                    label="Texto Principal" 
                    description="Títulos, contenido"
                  />
                  <ColorInput 
                    colorKey="texto_secundario" 
                    label="Texto Secundario" 
                    description="Labels, descripciones"
                  />
                </div>

                {/* Estados */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-slate-700 border-b pb-2">Estados</h4>
                  <ColorInput 
                    colorKey="error" 
                    label="Error" 
                    description="Alertas, validaciones"
                  />
                </div>
              </div>

              <Separator />

              {/* Vista Previa Mejorada */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Vista Previa</Label>
                <div 
                  className="rounded-xl border-2 overflow-hidden"
                  style={{ borderColor: formData.colores_tema.secundario }}
                >
                  {/* Header Preview */}
                  <div 
                    className="p-4 flex items-center justify-between"
                    style={{ backgroundColor: formData.colores_tema.terciario }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-white/20"></div>
                      <span className="text-white font-medium text-sm">Panel Admin</span>
                    </div>
                    <div 
                      className="px-3 py-1 rounded text-white text-xs font-medium"
                      style={{ backgroundColor: formData.colores_tema.primario }}
                    >
                      Botón Activo
                    </div>
                  </div>
                  
                  {/* Content Preview */}
                  <div 
                    className="p-4"
                    style={{ backgroundColor: formData.colores_tema.fondo }}
                  >
                    <div className="flex gap-4">
                      {/* Sidebar mini */}
                      <div 
                        className="w-16 rounded-lg p-2 space-y-2"
                        style={{ backgroundColor: formData.colores_tema.terciario }}
                      >
                        <div className="w-full h-8 rounded" style={{ backgroundColor: formData.colores_tema.primario }}></div>
                        <div className="w-full h-8 rounded bg-white/10"></div>
                        <div className="w-full h-8 rounded bg-white/10"></div>
                      </div>
                      
                      {/* Main content */}
                      <div className="flex-1 space-y-3">
                        <h3 
                          className="font-bold"
                          style={{ color: formData.colores_tema.texto }}
                        >
                          Título Principal
                        </h3>
                        <p 
                          className="text-sm"
                          style={{ color: formData.colores_tema.texto_secundario }}
                        >
                          Este es un texto secundario de ejemplo
                        </p>
                        
                        {/* Cards */}
                        <div className="grid grid-cols-3 gap-2">
                          <div 
                            className="p-3 rounded-lg"
                            style={{ backgroundColor: formData.colores_tema.superficie }}
                          >
                            <div className="text-xs" style={{ color: formData.colores_tema.texto_secundario }}>Métrica</div>
                            <div className="font-bold" style={{ color: formData.colores_tema.texto }}>125</div>
                          </div>
                          <div 
                            className="p-3 rounded-lg"
                            style={{ backgroundColor: formData.colores_tema.superficie }}
                          >
                            <div className="text-xs" style={{ color: formData.colores_tema.texto_secundario }}>Métrica</div>
                            <div className="font-bold" style={{ color: formData.colores_tema.texto }}>48</div>
                          </div>
                          <div 
                            className="p-3 rounded-lg"
                            style={{ backgroundColor: formData.colores_tema.superficie }}
                          >
                            <div className="text-xs" style={{ color: formData.colores_tema.texto_secundario }}>Métrica</div>
                            <div className="font-bold" style={{ color: formData.colores_tema.texto }}>89</div>
                          </div>
                        </div>
                        
                        {/* Buttons */}
                        <div className="flex gap-2 mt-3">
                          <div 
                            className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                            style={{ backgroundColor: formData.colores_tema.primario }}
                          >
                            Primario
                          </div>
                          <div 
                            className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                            style={{ backgroundColor: formData.colores_tema.secundario }}
                          >
                            Secundario
                          </div>
                          <div 
                            className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                            style={{ backgroundColor: formData.colores_tema.terciario }}
                          >
                            Terciario
                          </div>
                          <div 
                            className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                            style={{ backgroundColor: formData.colores_tema.error }}
                          >
                            Error
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logo */}
          <Card className="border-slate-200 lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Image className="w-5 h-5" />
                Logo de la Empresa
              </CardTitle>
              <CardDescription>Logo para el formulario público y panel de administración</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="w-32 h-32 bg-slate-100 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-300 overflow-hidden">
                  {empresa?.logo_url ? (
                    <img 
                      src={empresa.logo_url} 
                      alt="Logo" 
                      className="w-full h-full object-contain rounded-lg"
                    />
                  ) : (
                    <Image className="w-12 h-12 text-slate-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-600 mb-3">
                    Suba el logo de su empresa (JPG, PNG, GIF o WEBP, máx 2MB)
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    disabled={!isAdmin() || uploadingLogo} 
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="subir-logo-btn"
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Subir Logo
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Save Button */}
        {isAdmin() && (
          <div className="flex justify-end mt-6">
            <Button 
              type="submit" 
              disabled={saving}
              data-testid="guardar-empresa-btn"
              className="text-white px-8"
              style={{ 
                backgroundColor: formData.colores_tema.primario,
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = formData.colores_tema.secundario}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = formData.colores_tema.primario}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default EmpresaPage;
